import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle, Clock, AlertTriangle, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function CronSetupPage() {
  const navigate = useNavigate();
  const [isActivating, setIsActivating] = useState(false);
  const [result, setResult] = useState<any>(null);

  const activateCron = async () => {
    setIsActivating(true);
    toast.info('Activating daily cron job...');
    
    try {
      const { data, error } = await supabase.functions.invoke('setup-daily-cron');
      
      if (error) throw error;

      setResult({
        success: true,
        data,
        message: 'Daily cron job successfully activated!'
      });
      
      toast.success('Cron job activated!', {
        description: 'Daily automation will run at 6:00 PM AEST'
      });
    } catch (error: any) {
      setResult({
        success: false,
        error: error.message,
        message: 'Failed to activate cron job'
      });
      
      toast.error('Activation failed', {
        description: error.message
      });
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <div className="container mx-auto p-8 max-w-4xl">
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/automation')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Automation
          </Button>
        </div>
        <h1 className="text-4xl font-bold mb-2">Daily Cron Setup</h1>
        <p className="text-muted-foreground">Activate automatic daily content generation</p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            What This Does
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            This will set up a PostgreSQL cron job that automatically runs your content generation 
            pipeline every day at <strong className="text-primary">6:00 PM AEST</strong>.
          </p>

          <div className="bg-muted p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Daily Automation Process:</h4>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>Collect latest crypto market data from CoinGecko, Swyftx, CoinSpot</li>
              <li>Analyze 100+ tokens for investment opportunities</li>
              <li>Score and rank tokens using AI-powered analysis</li>
              <li>Generate 2 professional articles:
                <ul className="list-disc list-inside ml-6 mt-1">
                  <li>"Best Coin to Buy Today"</li>
                  <li>"High ROI Token Deep Dive"</li>
                </ul>
              </li>
              <li>Publish articles to your website</li>
              <li>Update token recommendations</li>
              <li>Distribute content on social media</li>
            </ol>
          </div>

          <Alert className="border-blue-600 bg-blue-50 dark:bg-blue-900/20">
            <AlertDescription className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <strong>Important:</strong> This setup only needs to be run ONCE. After activation, 
                the cron job will continue running daily until you disable it.
              </div>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Activate Daily Automation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!result && (
            <div>
              <Alert className="mb-4 border-green-600 bg-green-50 dark:bg-green-900/20">
                <AlertDescription className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <strong>Good news!</strong> The daily cron job is already active and running. Articles will be automatically generated every day at 6:00 PM AEST.
                    <div className="mt-2">
                      <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/admin/automation')}>
                        Go to Automation Control →
                      </Button>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
              
              <p className="text-muted-foreground mb-4">
                Click below to verify the cron status and see when the next run is scheduled.
              </p>
              
              <Button 
                onClick={activateCron}
                disabled={isActivating}
                size="lg"
                variant="outline"
                className="w-full md:w-auto"
              >
                {isActivating ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Checking Status...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-5 w-5" />
                    Verify Cron Status
                  </>
                )}
              </Button>
            </div>
          )}

          {result && (
            <Alert className={
              result.success 
                ? 'border-green-600 bg-green-50 dark:bg-green-900/20' 
                : 'border-red-600 bg-red-50 dark:bg-red-900/20'
            }>
              <AlertDescription>
                <div className="flex items-start gap-2">
                  {result.success ? (
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
                  )}
                  <div>
                    <div className="font-semibold mb-2">{result.message}</div>
                    {result.success ? (
                      <div className="text-sm space-y-2">
                        <div>✅ Cron job configured for daily execution at 6:00 PM AEST</div>
                        <div>✅ System will prevent duplicate runs</div>
                        <div>✅ First automated run will occur at the next scheduled time</div>
                        <div className="mt-4">
                          <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/admin/automation')}>
                            Go to Automation Control →
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm">
                        <div>Error: {result.error}</div>
                        <div className="mt-2">
                          Please check the edge function logs or try again.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Schedule Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Run Time</span>
              <span className="font-semibold">6:00 PM AEST Daily</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Timezone</span>
              <span className="font-semibold">Australian Eastern Standard Time</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Duration</span>
              <span className="font-semibold">3-5 minutes</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Duplicate Prevention</span>
              <span className="font-semibold">Max 1 run per 23 hours</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
